import { useRef, useState } from 'react';
import { parseApiError } from '../../helper/spreadsheetView';

// The grid owns this latch; Company Details keeps its independent #611 guard.
export default function useGridActions(setSnackbar) {
  const actionInFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const emailRequestsInFlight = useRef(new Set());
  const [pendingEmails, setPendingEmails] = useState(new Set());
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [actionsView, setActionsView] = useState('root');
  const [dataEnrichmentSelection, setDataEnrichmentSelection] = useState(null);

  const openActions = (event) => {
    if (actionInFlight.current) return;
    setActionsAnchor(event.currentTarget);
    setActionsView('root');
    setDataEnrichmentSelection(null);
  };

  const closeActions = () => {
    setActionsAnchor(null);
    setActionsView('root');
    setDataEnrichmentSelection(null);
  };

  // Guard paid grid submissions only; AI batch polling continues independently.
  const runPaidAction = async (submit) => {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setPending(true);
    try {
      return await submit();
    } catch (error) {
      const { msg } = parseApiError(error, 'Enrichment failed');
      setSnackbar({ open: true, message: msg, severity: 'error', persist: true });
    } finally {
      actionInFlight.current = false;
      setPending(false);
    }
  };

  // Dialog email verifications run concurrently, so they cannot share the grid's
  // single runPaidAction latch (#643). Guard per (rowId, email) instead.
  const emailRequestKey = (rowId, email) => JSON.stringify([rowId, email]);
  const isEmailPending = (rowId, email) => pendingEmails.has(emailRequestKey(rowId, email));

  // Ref-keyed so two clicks inside one React batch cannot both pass the check.
  const runEmailVerification = async (rowId, emails, submit) => {
    const keys = emails.map((email) => emailRequestKey(rowId, email));
    const inFlight = emailRequestsInFlight.current;
    if (keys.some((key) => inFlight.has(key))) return;
    keys.forEach((key) => inFlight.add(key));
    setPendingEmails(new Set(inFlight));
    try {
      await submit();
    } finally {
      keys.forEach((key) => inFlight.delete(key));
      setPendingEmails(new Set(inFlight));
    }
  };

  return {
    actionsAnchor,
    actionsView,
    dataEnrichmentSelection,
    setActionsView,
    setDataEnrichmentSelection,
    openActions,
    closeActions,
    pending,
    runPaidAction,
    isEmailPending,
    runEmailVerification,
  };
}
